import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { useUsers } from '@/hooks/use-users';
import type { AdminUser } from '@/types/user';

/**
 * Bestimmt Badge-Variante und Label anhand von Status + enabled-Flag.
 */
function statusBadge(user: AdminUser) {
  if (!user.enabled) {
    return <Badge variant="secondary">Deaktiviert</Badge>;
  }
  switch (user.status) {
    case 'CONFIRMED':
      return <Badge variant="success">Aktiv</Badge>;
    case 'FORCE_CHANGE_PASSWORD':
      return <Badge variant="warning">Passwort-Wechsel</Badge>;
    case 'RESET_REQUIRED':
      return <Badge variant="warning">Reset ausstehend</Badge>;
    case 'UNCONFIRMED':
      return <Badge variant="secondary">Unbestaetigt</Badge>;
    default:
      return <Badge variant="outline">{user.status}</Badge>;
  }
}

/** Menschenlesbarer Rollen-Name */
function roleName(role: string): string {
  switch (role) {
    case 'platform_admin':
      return 'Platform Admin';
    case 'merchant_admin':
      return 'Merchant Admin';
    case 'merchant_viewer':
      return 'Merchant Viewer';
    default:
      return role;
  }
}

export function UsersPage() {
  const navigate = useNavigate();
  const [emailFilter, setEmailFilter] = useState('');
  const [tokenStack, setTokenStack] = useState<string[]>([]);
  const currentToken = tokenStack.length > 0 ? tokenStack[tokenStack.length - 1] : undefined;

  const { data, isLoading, error, refetch } = useUsers({
    limit: 50,
    paginationToken: currentToken,
  });

  // Client-seitiger Email-Filter (bei wenigen Usern ausreichend, bei vielen Cognito Filter nutzen)
  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];
    if (!emailFilter.trim()) return data.users;
    const lowerFilter = emailFilter.toLowerCase();
    return data.users.filter((u) => u.email.toLowerCase().includes(lowerFilter));
  }, [data?.users, emailFilter]);

  const handleNextPage = useCallback((token: string) => {
    setTokenStack((prev) => [...prev, token]);
  }, []);

  const handleRowClick = useCallback(
    (sub: string) => navigate(`/users/${sub}`),
    [navigate],
  );

  return (
    <div>
      <PageHeader
        title="Benutzer"
        description="Cognito-Benutzer verwalten (Einladung, Rollen, Deaktivierung)"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Aktualisieren
            </Button>
            <Button size="sm" onClick={() => navigate('/users/new')}>
              <Plus className="mr-2 h-4 w-4" />
              Neuer Benutzer
            </Button>
          </div>
        }
      />

      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={5} cols={5} />
          </CardContent>
        </Card>
      )}

      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {!isLoading && !error && data && (
        <Card>
          {/* Email-Filter */}
          <div className="border-b px-4 py-3">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="E-Mail suchen…"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>E-Mail</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>Merchant-ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Erstellt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                    {emailFilter ? 'Kein Benutzer mit dieser E-Mail gefunden.' : 'Keine Benutzer gefunden.'}
                  </TableCell>
                </TableRow>
              )}
              {filteredUsers.map((user) => (
                <TableRow
                  key={user.sub}
                  className="cursor-pointer"
                  onClick={() => handleRowClick(user.sub)}
                >
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>{roleName(user.role)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {user.merchantId ?? '—'}
                  </TableCell>
                  <TableCell>{statusBadge(user)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(user.createdAt).toLocaleDateString('de-DE')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Forward-only Pagination */}
          {data.paginationToken && (
            <div className="flex justify-end border-t px-4 py-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleNextPage(data.paginationToken!)}
              >
                Naechste Seite
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
